import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Search, AlertTriangle, CheckCircle2 } from "lucide-react";
import { tourApi } from "@/lib/tourEntities";
import { driverApi } from "@/lib/entities";
import { apiError } from "@/lib/errors";

/**
 * TourSelectorModal — Correction Batch STEP 1.
 *
 * Opens from Drivers → Advanced Search when the operator clicks
 * either [Assign Driver] or [Add Co-Driver] on a row. Responsibilities:
 *   1. Show a searchable list of ELIGIBLE (upcoming/active, non-archived)
 *      tours only. Filter predicate mirrors the STEP-1 spec:
 *          is_archived == false
 *          AND (end_date IS NULL OR end_date >= today (browser local ISO))
 *   2. When a tour is selected, load its existing driver assignments so
 *      the operator can see current primary + existing co-drivers.
 *   3. Show an inline conflict preview by re-running the eligibility
 *      search scoped to the selected tour. `driverApi.search` already
 *      returns `overall_status` + `block_reasons` + `warning_reasons`
 *      for the (driver, tour) pair.
 *   4. On confirm, POST /api/tours/{tour_id}/drivers with the requested
 *      role. The server-side co-driver/eligibility rules from RC1 remain
 *      the source of truth — 409/422 responses are surfaced verbatim.
 *
 * The modal reuses the existing `perm("fleet.assign")` authorization on
 * the server. No new permission or route is introduced.
 */
const todayIso = () => new Date().toISOString().slice(0, 10);

const isUpcomingOrActive = (t) => {
  if (t.is_archived) return false;
  if (!t.end_date) return true;
  return t.end_date >= todayIso();
};

const ROLE_LABEL = {
  assign: { role: "primary_driver", title: "Assign Driver", verb: "Assign", suffix: "as primary driver" },
  co_driver: { role: "co_driver", title: "Add Co-Driver", verb: "Add", suffix: "as co-driver" },
};

export default function TourSelectorModal({
  open,
  driver,        // {id, driver_number, display_name}
  mode,          // "assign" | "co_driver"
  onClose,
  onAssigned,    // (tour, assignment) => void — parent may refresh
}) {
  const modeInfo = ROLE_LABEL[mode] || ROLE_LABEL.assign;

  const [q, setQ] = useState("");
  const [tours, setTours] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tourDrivers, setTourDrivers] = useState(null);
  const [conflictRow, setConflictRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset internal state whenever a new driver + mode is opened.
  useEffect(() => {
    if (!open) return;
    setQ("");
    setTours(null);
    setSelected(null);
    setTourDrivers(null);
    setConflictRow(null);
    setError(null);
    setSubmitting(false);
  }, [open, driver?.id, mode]);

  // Load upcoming/active tours (paginated by the server; we ask for 50 and
  // filter defensively for the eligibility predicate).
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        // date_from = today filters tours whose end_date >= today; the
        // server route already implements the exact overlap query.
        const r = await tourApi.list({
          q: q || undefined,
          date_from: todayIso(),
          page: 1,
          page_size: 50,
        });
        if (!cancel) setTours((r.results || []).filter(isUpcomingOrActive));
      } catch (e) {
        if (!cancel) {
          setTours([]);
          setError(apiError(e) || "Failed to load tours");
        }
      }
    }, 220);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, open]);

  // When a tour is selected: load its existing drivers + inline
  // conflict/eligibility for THIS driver. Both fire in parallel; either
  // failure is non-fatal (we still allow confirm and let the server 409).
  useEffect(() => {
    if (!selected || !driver) { setTourDrivers(null); setConflictRow(null); return; }
    let cancel = false;
    (async () => {
      try {
        const rows = await tourApi.listDrivers(selected.id);
        if (!cancel) setTourDrivers(rows.filter((r) => !r.is_archived));
      } catch {
        if (!cancel) setTourDrivers([]);
      }
      try {
        const s = await driverApi.search({
          tour_id: selected.id,
          q: driver.driver_number,
          page_size: 5,
          include_ineligible: true,
        });
        const match = (s.results || []).find((r) => r.driver?.id === driver.id) || null;
        if (!cancel) setConflictRow(match);
      } catch {
        if (!cancel) setConflictRow(null);
      }
    })();
    return () => { cancel = true; };
  }, [selected?.id, driver?.id]);

  const existingPrimary = useMemo(
    () => (tourDrivers || []).find((r) => r.role === "primary_driver") || null,
    [tourDrivers]
  );
  const existingCoDrivers = useMemo(
    () => (tourDrivers || []).filter((r) => r.role === "co_driver"),
    [tourDrivers]
  );

  const eligibility = conflictRow?.eligibility || null;
  const isHardBlocked = !!eligibility?.hard_blocked;
  // "primary_as_co_driver" cannot be pre-detected client-side without
  // more state — we rely on the server's structured 409 on confirm.
  const confirmDisabled = !selected || submitting || isHardBlocked;

  const doConfirm = async () => {
    if (!selected || !driver) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        driver_id: driver.id,
        role: modeInfo.role,
        start_date: selected.start_date,
        end_date: selected.end_date,
        // Operators pressing Confirm from Advanced Search have implicitly
        // acknowledged non-blocking eligibility warnings shown inline.
        warnings_acknowledged: true,
      };
      const row = await tourApi.addDriver(selected.id, body);
      toast.success(`${modeInfo.verb}ed ${driver.display_name} to ${selected.tour_number}`);
      onAssigned?.(selected, row);
      onClose?.();
    } catch (e) {
      // Surface the server's structured reason verbatim.
      const detail = e?.response?.data?.detail;
      let msg;
      if (detail && typeof detail === "object") {
        const parts = [detail.code, detail.message].filter(Boolean).join(" · ");
        const brs = Array.isArray(detail.block_reasons) ? detail.block_reasons.join(", ") : "";
        msg = brs ? `${parts} — ${brs}` : parts;
      } else {
        msg = apiError(e) || "Failed to assign driver";
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-selector-title"
      data-testid="tour-selector-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-[2px] p-3"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white border border-coach-silver rounded-md shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-coach-silver">
          <div>
            <h2 id="tour-selector-title"
                className="text-[15px] font-semibold text-carbon tracking-tight"
                data-testid="tour-selector-title">
              {modeInfo.title}
            </h2>
            <div className="text-[12px] text-steel mt-0.5">
              {driver ? <>Select a tour for <span className="text-carbon font-medium">{driver.display_name}</span> <span className="font-mono text-[11px]">({driver.driver_number})</span></> : "Select a tour"}
            </div>
          </div>
          <button
            type="button"
            data-testid="tour-selector-close"
            onClick={onClose}
            className="text-steel hover:text-carbon p-1 rounded-sm hover:bg-road-white"
            aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col md:flex-row min-h-0 flex-1">
          {/* Tour picker */}
          <div className="md:w-1/2 border-b md:border-b-0 md:border-r border-coach-silver flex flex-col min-h-0">
            <div className="p-3 border-b border-coach-silver">
              <div className="relative">
                <Search size={13} className="absolute left-2 top-2.5 text-steel" />
                <input
                  data-testid="tour-selector-search"
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search tour # / name / artist…"
                  className="w-full border border-coach-silver rounded-sm pl-7 pr-2 py-1.5 text-[12.5px]"
                />
              </div>
              <div className="text-[10.5px] uppercase tracking-widest text-steel mt-2">
                Upcoming & active tours only
              </div>
            </div>
            <div className="overflow-y-auto flex-1" data-testid="tour-selector-list">
              {tours === null && (
                <div className="text-center text-steel text-[12.5px] py-8">Loading…</div>
              )}
              {tours && tours.length === 0 && (
                <div className="text-center text-steel text-[12.5px] py-8" data-testid="tour-selector-empty">
                  No upcoming tours match.
                </div>
              )}
              {(tours || []).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`tour-selector-option-${t.tour_number}`}
                  onClick={() => setSelected(t)}
                  className={
                    "w-full text-left px-3 py-2 text-[12.5px] border-b border-coach-silver/40 hover:bg-road-white " +
                    (selected?.id === t.id ? "bg-coach-blue/10" : "")
                  }
                >
                  <div className="font-mono text-[11px] text-steel">{t.tour_number}</div>
                  <div className="text-carbon truncate">{t.display_name}</div>
                  <div className="text-[11px] text-steel">{t.artist_display || "—"} · {t.start_date} → {t.end_date}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Preview + confirm */}
          <div className="md:w-1/2 flex flex-col min-h-0">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-[12.5px] text-steel p-6 text-center">
                Choose a tour to preview current drivers and confirm the assignment.
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="tour-selector-preview">
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Selected tour</div>
                  <div className="text-[13.5px] text-carbon font-medium">{selected.tour_number} · {selected.display_name}</div>
                  <div className="text-[11.5px] text-steel">{selected.start_date} → {selected.end_date}</div>
                </div>
                <div className="border border-coach-silver rounded-sm p-2.5" data-testid="tour-selector-current-primary">
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Current primary driver</div>
                  <div className="text-[12.5px] text-carbon mt-0.5">
                    {tourDrivers === null ? "Loading…" : existingPrimary
                      ? <>{existingPrimary.driver_display_name || existingPrimary.driver_id?.slice(0, 8)}</>
                      : <span className="text-steel">— none —</span>}
                  </div>
                </div>
                <div className="border border-coach-silver rounded-sm p-2.5" data-testid="tour-selector-current-co-drivers">
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Current co-drivers</div>
                  <div className="text-[12.5px] text-carbon mt-0.5">
                    {tourDrivers === null ? "Loading…" : existingCoDrivers.length === 0
                      ? <span className="text-steel">— none —</span>
                      : <ul className="list-disc list-inside space-y-0.5">
                          {existingCoDrivers.map((c) => (
                            <li key={c.id}>{c.driver_display_name || c.driver_id?.slice(0, 8)}</li>
                          ))}
                        </ul>}
                  </div>
                </div>

                {conflictRow && (
                  <div
                    data-testid="tour-selector-conflict-preview"
                    className={
                      "border rounded-sm p-2.5 " +
                      (isHardBlocked
                        ? "border-critical-red bg-critical-red/5"
                        : (eligibility?.warning_reasons?.length ? "border-amber-hold bg-amber-hold/10" : "border-touring-lime bg-touring-lime/10"))
                    }
                  >
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest">
                      {isHardBlocked
                        ? <AlertTriangle size={12} className="text-critical-red" />
                        : (eligibility?.warning_reasons?.length ? <AlertTriangle size={12} className="text-amber-hold" /> : <CheckCircle2 size={12} className="text-touring-lime" />)}
                      <span className={isHardBlocked ? "text-critical-red" : (eligibility?.warning_reasons?.length ? "text-amber-hold" : "text-carbon")}>
                        {isHardBlocked ? "Blocked" : (eligibility?.warning_reasons?.length ? "Warning" : "Clear")}
                      </span>
                    </div>
                    {isHardBlocked && (eligibility?.block_reasons || []).length > 0 && (
                      <ul className="mt-1 list-disc list-inside text-[12px] text-carbon">
                        {eligibility.block_reasons.map((r, i) => (
                          <li key={i}>{r.message || r.code || String(r)}</li>
                        ))}
                      </ul>
                    )}
                    {!isHardBlocked && (eligibility?.warning_reasons || []).length > 0 && (
                      <ul className="mt-1 list-disc list-inside text-[12px] text-carbon">
                        {eligibility.warning_reasons.map((r, i) => (
                          <li key={i}>{r.message || r.code || String(r)}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {error && (
                  <div className="border border-critical-red bg-critical-red/5 rounded-sm p-2.5 text-[12px] text-critical-red"
                       data-testid="tour-selector-error">
                    {error}
                  </div>
                )}
              </div>
            )}

            <div className="border-t border-coach-silver px-4 py-3 flex items-center justify-end gap-2 bg-road-white">
              <button
                type="button"
                data-testid="tour-selector-cancel"
                onClick={onClose}
                className="px-3 py-1.5 text-[12.5px] text-carbon border border-coach-silver rounded-sm hover:bg-white">
                Cancel
              </button>
              <button
                type="button"
                data-testid="tour-selector-confirm"
                disabled={confirmDisabled}
                onClick={doConfirm}
                className="px-3 py-1.5 text-[12.5px] bg-coach-blue text-white rounded-sm hover:bg-deep-road-navy disabled:opacity-40 disabled:cursor-not-allowed">
                {submitting ? "Assigning…" : `${modeInfo.verb} ${modeInfo.suffix}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
